/**
 * Bundle Add-to-Cart (Metafield-driven, Loop Widget Integration)
 *
 * Works with Loop's subscription widget on the main product page.
 * When a customer selects "Subscribe and Save", a free refill product
 * is automatically added alongside the main product.
 *
 * Behaviour:
 *   - One-time purchase: Only the main product is added to cart.
 *   - Subscribe: The main product is added as one-time, plus a refill
 *     product is added with a subscription selling plan (100% off first
 *     order, 10% off recurring). The selling plan from Loop's widget is
 *     read to determine the customer's choice but is NOT applied to the
 *     main product.
 *
 * Both bundle items are tagged with _bundle_id and _bundle_role line item
 * properties (hidden from customers) so the cart monitor script can
 * identify and remove orphaned refills if the main product is removed.
 *
 * This script also injects compare-at pricing into Loop's subscribe option,
 * showing the combined value (main + refill) as a struck-through price
 * with a discount badge.
 *
 * CONFIGURATION:
 * Reads config from a <script type="application/json" id="bundle-config">
 * tag rendered by the bundle-config.liquid snippet. Config values are
 * driven by product metafields — no hardcoded IDs in this file.
 *
 * DEPENDENCIES:
 * - bundle-config.liquid (Liquid snippet outputting JSON config + compare-at element)
 * - bundle-cart-monitor.js (removes orphaned refills from cart)
 * - Loop Subscriptions widget (provides the one-time vs subscribe UI)
 * - Baseline theme by Switch (cart drawer events, Alpine.js form handling)
 */

(function () {
  // ============================================================
  // READ CONFIGURATION FROM LIQUID
  // ============================================================

  function getConfig() {
    var configEl = document.getElementById('bundle-config');
    if (!configEl) return null;

    try {
      var config = JSON.parse(configEl.textContent);

      if (!config.refillVariantId) {
        console.warn('Bundle config: missing refillVariantId');
        return null;
      }

      return {
        refillVariantId: parseInt(config.refillVariantId),
        refillSellingPlans: config.refillSellingPlans || [],
        refillQuantity: parseInt(config.refillQuantity) || 1,
        bundleId: config.bundleId || null,
      };
    } catch (error) {
      console.error('Bundle config: failed to parse', error);
      return null;
    }
  }

  // ============================================================
  // ADD TO CART LOGIC
  // ============================================================

  /**
   * Builds the cart payload for the refill item with the subscription
   * selling plan and bundle identification properties.
   */
  function buildRefillItem(config, refillSellingPlanId) {
    var refillItem = {
      id: config.refillVariantId,
      quantity: config.refillQuantity,
      properties: {
        _bundle_id: config.bundleId,
        _bundle_role: 'refill',
      },
    };

    if (refillSellingPlanId) {
      refillItem.selling_plan = refillSellingPlanId;
    }

    return refillItem;
  }

  /**
   * Intercepts the add-to-cart action on the main product page.
   *
   * If the customer selected subscribe via Loop's widget:
   *   - Adds the main product as one-time (no selling plan)
   *   - Adds the refill with the subscription selling plan
   *   - Both items get _bundle_id and _bundle_role properties
   *
   * If the customer selected one-time:
   *   - Adds only the main product with no special properties
   *
   * After adding, fetches updated cart sections and dispatches
   * Baseline's cart events to refresh the cart drawer.
   */
  async function handleBundleAddToCart(form, button, config) {
    if (button) {
      button.disabled = true;
      button.classList.add('bundle-loading');
    }

    try {
      var formData = new FormData(form);
      var mainProductId = formData.get('id');
      var mainProductQuantity = formData.get('quantity') || 1;

      // Read Loop's widget selection to determine one-time vs subscribe
      var sellingPlanInput = form.querySelector('input[name="selling_plan"]');
      var sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : null;
      var customerChoseSubscribe = !!sellingPlanValue;

      var refillSellingPlanId = null;

      if (customerChoseSubscribe) {
        var loopDropdown = document.querySelector('.loop-widget-sp-selector');
        if (loopDropdown) {
          var selectedOption = loopDropdown.options[loopDropdown.selectedIndex];
          var selectedName = selectedOption ? selectedOption.textContent.trim() : null;

          if (selectedName) {
            config.refillSellingPlans.forEach(function (plan) {
              if (plan.name === selectedName) {
                refillSellingPlanId = plan.id;
              }
            });
          }
        }
      }

      // Build the cart payload
      var mainItem = {
        id: parseInt(mainProductId),
        quantity: parseInt(mainProductQuantity),
      };

      // Only add bundle properties when subscribing
      if (customerChoseSubscribe) {
        mainItem.properties = {
          _bundle_id: config.bundleId,
          _bundle_role: 'main',
        };
      }

      var items = [mainItem];

      // Add the refill only when subscribing
      if (customerChoseSubscribe && refillSellingPlanId) {
        items.push(buildRefillItem(config, refillSellingPlanId));
      }

      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: items }),
      });

      if (!response.ok) {
        var errorData = await response.json();
        throw new Error(
          errorData.description || 'Failed to add items to cart'
        );
      }

      var data = await response.json();

      // Reset button
      if (button) {
        button.disabled = false;
        button.classList.remove('bundle-loading');
      }

      // Fetch updated cart data and sections for the drawer
      var cartResponse = await fetch('/cart.js');
      var cart = await cartResponse.json();
      window.theme.cartItemCount = cart.item_count;

      var sectionsResponse = await fetch(
        '/?sections=cart-items,cart-live-region,cart-item-count,cart-footer'
      );
      var sections = await sectionsResponse.json();

      var eventDetail = {
        detail: {
          response: {
            sections: sections,
            key: data.items[0].key,
            variant_id: data.items[0].variant_id,
          },
          sourceId: form.id,
        },
      };

      // Dispatch on window for Baseline's cart-items component
      window.dispatchEvent(
        new CustomEvent('baseline:cart:afteradditem', eventDetail)
      );

      // Dispatch on document.body for Baseline's ModalCart to open the drawer
      document.body.dispatchEvent(
        new CustomEvent('baseline:cart:afteradditem', eventDetail)
      );
    } catch (error) {
      console.error('Bundle: error adding to cart', error);
      alert(
        'Sorry, there was a problem adding to your cart. Please try again.'
      );

      if (button) {
        button.disabled = false;
        button.classList.remove('bundle-loading');
      }
    }
  }

  // ============================================================
  // COMPARE-AT PRICE INJECTION
  // ============================================================

  /**
   * Injects compare-at pricing into Loop's subscribe widget option.
   * Shows the combined price (main + refill) as a struck-through price
   * alongside the actual price, and activates Loop's discount badge
   * to show the saving amount.
   *
   * Runs on a MutationObserver to re-apply whenever Loop re-renders
   * its widget (e.g. when toggling between one-time and subscribe).
   */
  function initCompareAtPrice() {
    var compareEl = document.getElementById('bundle-compare-price');
    if (!compareEl) return;

    var wasPrice = compareEl.querySelector('.bundle-compare-was s').textContent.trim();
    var nowPrice = compareEl.querySelector('.bundle-compare-now').textContent.trim();
    var savingText = compareEl.querySelector('.bundle-compare-saving').textContent.trim();

    // Remove the standalone element — pricing is injected into Loop's widget
    compareEl.remove();

    function updateLoopWidget() {
      var subscribeOption = document.querySelector(
        '[data-loop-widget-selling-plan-group]'
      );
      if (!subscribeOption) return;

      // Update the price display with struck-through combined price
      var priceEl = subscribeOption.querySelector(
        '.loop-widget-purchase-option-price'
      );
      if (priceEl && priceEl.innerHTML.indexOf('<s') === -1) {
        priceEl.innerHTML =
          '<s>' +
          wasPrice +
          '</s> ' +
          nowPrice;
      }

      // Show the discount badge with saving amount
      var badge = subscribeOption.querySelector(
        '.loop-widget-purchase-option-discount-badge'
      );
      if (badge && badge.classList.contains('loop-display-none')) {
        badge.classList.remove('loop-display-none');
        badge.textContent = savingText;
      }
    }

    // Watch for Loop re-renders and re-apply changes
    var observer = new MutationObserver(function () {
      updateLoopWidget();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    updateLoopWidget();
  }

  // ============================================================
  // INITIALISATION
  // ============================================================

  /**
   * Attaches click and submit interceptors to the product form.
   * Uses capture phase (useCapture: true) and stopImmediatePropagation()
   * to ensure this runs before Baseline's Alpine.js handlers.
   */
  function attachListeners(config) {
    var productForm = document.querySelector('form[action="/cart/add"]');
    if (!productForm) return;

    var submitButton = productForm.querySelector('button[type="submit"]');
    if (!submitButton) return;

    submitButton.addEventListener(
      'click',
      function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        handleBundleAddToCart(productForm, submitButton, config);
      },
      true
    );

    productForm.addEventListener(
      'submit',
      function (e) {
        e.preventDefault();
        e.stopImmediatePropagation();
      },
      true
    );
  }

  /**
   * Waits for Loop's subscription widget to inject its selling_plan
   * hidden input into the product form before attaching event listeners.
   * Falls back to initialising after 10 seconds if Loop doesn't render.
   */
  function waitForLoopWidget(config) {
    var sellingPlanInput = document.querySelector(
      'form[action="/cart/add"] input[name="selling_plan"]'
    );

    if (sellingPlanInput) {
      attachListeners(config);
      return;
    }

    var observer = new MutationObserver(function () {
      var input = document.querySelector(
        'form[action="/cart/add"] input[name="selling_plan"]'
      );
      if (input) {
        observer.disconnect();
        attachListeners(config);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(function () {
      observer.disconnect();
      attachListeners(config);
    }, 10000);
  }

  /**
   * Entry point. Reads config, waits for Loop's widget, attaches
   * listeners, and initialises compare-at price injection.
   * Exits silently if no bundle-config element is found on the page.
   */
  function init() {
    var config = getConfig();
    if (!config) return;

    waitForLoopWidget(config);
    initCompareAtPrice();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();